import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import deDE from "antd/locale/de_DE";
import enUS from "antd/locale/en_US";
import { useTranslation } from "react-i18next";
import { buildTheme, cssVariables, useColorMode, saufdings } from "@dowty/ui";

const DesignModeContext = createContext<{ mode: "light" | "dark"; toggle: () => void } | null>(null);

export const useDesignMode = () => {
    const mode = useContext(DesignModeContext);
    if (!mode) throw new Error("useDesignMode must be used inside DesignModeProvider");
    return mode;
};

export const DesignModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { i18n } = useTranslation();
    const { mode, setSetting } = useColorMode("saufdings_theme", saufdings);
    const toggle = useCallback(() => setSetting(mode === "dark" ? "light" : "dark"), [mode, setSetting]);
    const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

    useEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = mode;
        root.style.colorScheme = mode;
        Object.entries(cssVariables(saufdings, mode)).forEach(([key, value]) => root.style.setProperty(key, value));
    }, [mode]);

    return (
        <DesignModeContext.Provider value={value}>
            <ConfigProvider theme={buildTheme(saufdings, mode)} locale={(i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("de") ? deDE : enUS}>
                <AntdApp message={{ getContainer: () => document.getElementById("ui-main") ?? document.body }}>{children}</AntdApp>
            </ConfigProvider>
        </DesignModeContext.Provider>
    );
};
